import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Activity } from 'lucide-react'

interface Etapa5Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

const ATIVIDADES_FUNCIONAIS = [
  { id: 'alimentacao', nome: 'Alimentação', descricao: 'Capacidade de se alimentar sem ajuda' },
  { id: 'banho', nome: 'Banho/Higiene', descricao: 'Capacidade de tomar banho e fazer higiene pessoal' },
  { id: 'vestir', nome: 'Vestir-se', descricao: 'Capacidade de vestir-se sozinho(a)' },
  { id: 'higiene_intima', nome: 'Higiene Íntima', descricao: 'Capacidade de fazer higiene íntima' },
  { id: 'caminhar', nome: 'Caminhar', descricao: 'Capacidade de andar sem ajuda' },
  { id: 'levantar', nome: 'Levantar', descricao: 'Capacidade de levantar da cama/cadeira' },
  { id: 'sentar', nome: 'Sentar', descricao: 'Capacidade de sentar-se corretamente' },
  { id: 'banheiro', nome: 'Usar o Banheiro', descricao: 'Capacidade de usar o banheiro sozinho(a)' },
  { id: 'continencia_urinaria', nome: 'Continência Urinária', descricao: 'Controle da bexiga' },
  { id: 'continencia_fecal', nome: 'Continência Fecal', descricao: 'Controle intestinal' },
  { id: 'medicamentos', nome: 'Administrar Medicamentos', descricao: 'Capacidade de tomar medicamentos sozinho(a)' }
]

const NIVEIS_DEPENDENCIA = [
  { value: 'independente', label: 'Independente', descricao: 'Realiza sozinho(a) sem dificuldade' },
  { value: 'supervisao', label: 'Precisa de Supervisão', descricao: 'Consegue fazer, mas precisa de alguém por perto' },
  { value: 'ajuda_parcial', label: 'Ajuda Parcial', descricao: 'Precisa de ajuda para parte da atividade' },
  { value: 'dependencia_total', label: 'Dependência Total', descricao: 'Não consegue fazer, precisa de ajuda completa' }
]

export function Etapa5CapacidadeFuncional({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa5Props) {
  const funcionalidade = formData.funcionalidade || []

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar declaração de veracidade
    if (!formData.veracidade_accept) {
      errors.push('Declaração de veracidade')
    }

    // Validar todas as atividades funcionais
    const atividadesSemNivel: string[] = []
    ATIVIDADES_FUNCIONAIS.forEach(atividade => {
      const item = funcionalidade.find((f: any) => f.atividade === atividade.id)
      if (!item || !item.nivel_dependencia || item.nivel_dependencia === '') {
        atividadesSemNivel.push(atividade.nome)
      }
    })

    if (atividadesSemNivel.length > 0) {
      errors.push(`Selecione o nível de dependência para: ${atividadesSemNivel.join(', ')}`)
    }

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  const handleFuncionalidadeChange = (atividadeId: string, nivel: string) => {
    const newFuncionalidade = funcionalidade.map((f: any) =>
      f.atividade === atividadeId ? { ...f, nivel_dependencia: nivel } : f
    )

    // Se não existir, adicionar
    if (!funcionalidade.some((f: any) => f.atividade === atividadeId)) {
      newFuncionalidade.push({
        atividade: atividadeId,
        nivel_dependencia: nivel,
        observacoes: ''
      })
    }

    onChange('funcionalidade', newFuncionalidade)
  }

  const handleObservacoesChange = (atividadeId: string, observacoes: string) => {
    const newFuncionalidade = funcionalidade.map((f: any) =>
      f.atividade === atividadeId ? { ...f, observacoes } : f
    )

    // Se não existir, adicionar
    if (!funcionalidade.some((f: any) => f.atividade === atividadeId)) {
      newFuncionalidade.push({
        atividade: atividadeId,
        nivel_dependencia: '',
        observacoes
      })
    }

    onChange('funcionalidade', newFuncionalidade)
  }

  const getNivelDependencia = (atividadeId: string) => {
    const item = funcionalidade.find((f: any) => f.atividade === atividadeId)
    return item?.nivel_dependencia || ''
  }

  const getObservacoes = (atividadeId: string) => {
    const item = funcionalidade.find((f: any) => f.atividade === atividadeId)
    return item?.observacoes || ''
  }

  // Calcular score funcional
  const calcularScore = () => {
    // 11 atividades no total. Se todas independentes = 100 pts.
    // Cada atividade independente vale ~9.09 pts.
    // Vamos usar pontos positivos para facilitar a lógica.
    const pontosPorNivel = {
      independente: 9.09,
      supervisao: 6,
      ajuda_parcial: 3,
      dependencia_total: 0
    }

    let scoreTotal = 0
    funcionalidade.forEach((f: any) => {
      if (f.nivel_dependencia && f.nivel_dependencia !== '') {
        scoreTotal += pontosPorNivel[f.nivel_dependencia as keyof typeof pontosPorNivel] || 0
      }
    })

    // Se houver atividades não preenchidas, elas contam como 0 (dependência total) para o score atual
    return Math.round(scoreTotal)
  }

  const score = calcularScore()

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Avaliação Funcional
          </CardTitle>
          <p className="text-sm text-gray-600">
            Avalie a capacidade do idoso(a) para realizar cada atividade diária.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {ATIVIDADES_FUNCIONAIS.map((atividade) => (
            <div key={atividade.id} className="border rounded-lg p-4">
              <div className="mb-3">
                <h4 className="font-semibold">{atividade.nome}</h4>
                <p className="text-sm text-gray-600">{atividade.descricao}</p>
              </div>

              <RadioGroup
                value={getNivelDependencia(atividade.id)}
                onValueChange={(value) => handleFuncionalidadeChange(atividade.id, value)}
                className="space-y-2"
                required
              >
                {NIVEIS_DEPENDENCIA.map((nivel) => (
                  <div key={nivel.value} className="flex items-start space-x-2">
                    <RadioGroupItem value={nivel.value} id={`${atividade.id}-${nivel.value}`} />
                    <div className="flex-1">
                      <Label htmlFor={`${atividade.id}-${nivel.value}`} className="text-sm font-medium">
                        {nivel.label}
                      </Label>
                      <p className="text-xs text-gray-500">{nivel.descricao}</p>
                    </div>
                  </div>
                ))}
              </RadioGroup>

              <div className="mt-3">
                <Label className="text-sm">Observações (opcional)</Label>
                <Textarea
                  value={getObservacoes(atividade.id)}
                  onChange={(e) => handleObservacoesChange(atividade.id, e.target.value)}
                  placeholder="Adicione detalhes importantes sobre esta atividade"
                  className="mt-1 text-sm"
                  rows={2}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
        <p className="text-sm text-blue-800">
          <strong>Score Funcional Atual: {score}/100</strong>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Resumo da Avaliação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-600">
                {funcionalidade.filter((f: any) => f.nivel_dependencia === 'independente').length}
              </div>
              <div className="text-sm text-green-800">Independentes</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-600">
                {funcionalidade.filter((f: any) => f.nivel_dependencia === 'supervisao').length}
              </div>
              <div className="text-sm text-blue-800">Supervisão</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-yellow-600">
                {funcionalidade.filter((f: any) => f.nivel_dependencia === 'ajuda_parcial').length}
              </div>
              <div className="text-sm text-yellow-800">Ajuda Parcial</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-600">
                {funcionalidade.filter((f: any) => f.nivel_dependencia === 'dependencia_total').length}
              </div>
              <div className="text-sm text-red-800">Dependência Total</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e 
              foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade"
                checked={formData.veracidade_accept || false}
                onCheckedChange={(checked) => onChange('veracidade_accept', checked as boolean)}
                className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
              />
              <Label htmlFor="veracidade" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
